import React, { useState, useMemo } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Autocomplete,
    IconButton,
    Typography,
    Button,
    InputAdornment,
    Tooltip,
    Paper,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    DragIndicator as DragIcon,
    ContentCopy as CopyIcon,
} from '@mui/icons-material';

interface InventoryItem {
    id: string;
    name: string;
    description?: string;
    price: number;
    hsnCode?: string;
    gstRate?: number;
    unit?: string;
}

interface InvoiceItem {
    id: string;
    inventoryItemId?: string;
    name: string;
    description?: string;
    hsnCode: string;
    quantity: number;
    rate: number;
    gstRate: number;
    discount: number;
    amount: number;
    taxAmount: number;
    total: number;
}

interface InlineItemsTableProps {
    items: InvoiceItem[];
    inventory: InventoryItem[];
    onItemsChange: (items: InvoiceItem[]) => void;
    isGSTApplicable: boolean;
}

const InlineItemsTable: React.FC<InlineItemsTableProps> = ({
    items,
    inventory,
    onItemsChange,
    isGSTApplicable,
}) => {
    const [editingId, setEditingId] = useState<string | null>(null);

    // Calculate item totals
    const calculateItemTotals = (item: Partial<InvoiceItem>): InvoiceItem => {
        const quantity = item.quantity || 0;
        const rate = item.rate || 0;
        const gstRate = item.gstRate || 0;
        const discount = item.discount || 0;

        const amount = quantity * rate;
        const discountedAmount = amount - discount;
        const taxAmount = (discountedAmount * gstRate) / 100;
        const total = discountedAmount + taxAmount;

        return {
            id: item.id || `item-${Date.now()}`,
            inventoryItemId: item.inventoryItemId,
            name: item.name || '',
            description: item.description || '',
            hsnCode: item.hsnCode || '',
            quantity,
            rate,
            gstRate,
            discount,
            amount: discountedAmount,
            taxAmount,
            total,
        };
    };

    // Add new item
    const handleAddItem = () => {
        const newItem = calculateItemTotals({
            id: `item-${Date.now()}`,
            name: '',
            hsnCode: '',
            quantity: 1,
            rate: 0,
            gstRate: 18,
            discount: 0,
        });
        onItemsChange([...items, newItem]);
        setEditingId(newItem.id);
    };

    // Update item
    const handleUpdateItem = (id: string, updates: Partial<InvoiceItem>) => {
        const updatedItems = items.map(item =>
            item.id === id ? calculateItemTotals({ ...item, ...updates }) : item
        );
        onItemsChange(updatedItems);
    };

    // Delete item
    const handleDeleteItem = (id: string) => {
        onItemsChange(items.filter(item => item.id !== id));
    };

    // Duplicate item
    const handleDuplicateItem = (id: string) => {
        const itemToDuplicate = items.find(item => item.id === id);
        if (itemToDuplicate) {
            const duplicatedItem = {
                ...itemToDuplicate,
                id: `item-${Date.now()}`,
            };
            onItemsChange([...items, duplicatedItem]);
        }
    };

    // Select inventory item
    const handleSelectInventoryItem = (id: string, inventoryItem: InventoryItem | null) => {
        if (inventoryItem) {
            handleUpdateItem(id, {
                inventoryItemId: inventoryItem.id,
                name: inventoryItem.name,
                description: inventoryItem.description,
                hsnCode: inventoryItem.hsnCode || '',
                rate: inventoryItem.price,
                gstRate: inventoryItem.gstRate || 18,
            });
        }
    };

    // Format currency
    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2,
        }).format(amount);
    };

    return (
        <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Items & Services
            </Typography>

            <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                    mb: 2,
                    '& .MuiTableCell-root': {
                        py: 1.5,
                    },
                }}
            >
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                            <TableCell width={30}></TableCell>
                            <TableCell width="30%">Item / Service</TableCell>
                            <TableCell width="10%">HSN/SAC</TableCell>
                            <TableCell width="10%" align="right">Qty</TableCell>
                            <TableCell width="12%" align="right">Rate</TableCell>
                            <TableCell width="10%" align="right">GST %</TableCell>
                            <TableCell width="12%" align="right">Discount</TableCell>
                            <TableCell width="12%" align="right">Amount</TableCell>
                            <TableCell width="10%" align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {items.map((item, index) => (
                            <TableRow
                                key={item.id}
                                sx={{
                                    '&:hover': {
                                        bgcolor: 'action.hover',
                                    },
                                }}
                            >
                                <TableCell>
                                    <DragIcon sx={{ color: 'action.disabled', cursor: 'grab' }} />
                                </TableCell>

                                {/* Item Name with Autocomplete */}
                                <TableCell>
                                    <Autocomplete<InventoryItem>
                                        options={inventory}
                                        getOptionLabel={(option) => option.name}
                                        value={inventory.find(inv => inv.id === item.inventoryItemId) || null}
                                        onChange={(e, value) => handleSelectInventoryItem(item.id, value)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                size="small"
                                                placeholder="Search or type item name..."
                                            />
                                        )}
                                        sx={{ minWidth: 200 }}
                                    />
                                    {item.description && (
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                            {item.description}
                                        </Typography>
                                    )}
                                </TableCell>

                                {/* HSN Code */}
                                <TableCell>
                                    <TextField
                                        size="small"
                                        value={item.hsnCode}
                                        onChange={(e) => handleUpdateItem(item.id, { hsnCode: e.target.value })}
                                        placeholder="HSN"
                                        fullWidth
                                    />
                                </TableCell>

                                {/* Quantity */}
                                <TableCell align="right">
                                    <TextField
                                        size="small"
                                        type="number"
                                        value={item.quantity}
                                        onChange={(e) => handleUpdateItem(item.id, { quantity: parseFloat(e.target.value) || 0 })}
                                        inputProps={{ min: 0, step: 0.01 }}
                                        sx={{ width: 80 }}
                                    />
                                </TableCell>

                                {/* Rate */}
                                <TableCell align="right">
                                    <TextField
                                        size="small"
                                        type="number"
                                        value={item.rate}
                                        onChange={(e) => handleUpdateItem(item.id, { rate: parseFloat(e.target.value) || 0 })}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                        }}
                                        inputProps={{ min: 0, step: 0.01 }}
                                        sx={{ width: 100 }}
                                    />
                                </TableCell>

                                {/* GST Rate */}
                                <TableCell align="right">
                                    <TextField
                                        size="small"
                                        type="number"
                                        value={item.gstRate}
                                        onChange={(e) => handleUpdateItem(item.id, { gstRate: parseFloat(e.target.value) || 0 })}
                                        InputProps={{
                                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                        }}
                                        inputProps={{ min: 0, max: 100, step: 0.01 }}
                                        sx={{ width: 80 }}
                                    />
                                </TableCell>

                                {/* Discount */}
                                <TableCell align="right">
                                    <TextField
                                        size="small"
                                        type="number"
                                        value={item.discount}
                                        onChange={(e) => handleUpdateItem(item.id, { discount: parseFloat(e.target.value) || 0 })}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                        }}
                                        inputProps={{ min: 0, step: 0.01 }}
                                        sx={{ width: 100 }}
                                    />
                                </TableCell>

                                {/* Amount (calculated) */}
                                <TableCell align="right">
                                    <Typography variant="body2" fontWeight="medium">
                                        {formatCurrency(item.total)}
                                    </Typography>
                                    {item.taxAmount > 0 && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                            Tax: {formatCurrency(item.taxAmount)}
                                        </Typography>
                                    )}
                                </TableCell>

                                {/* Actions */}
                                <TableCell align="center">
                                    <Tooltip title="Duplicate">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleDuplicateItem(item.id)}
                                        >
                                            <CopyIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => handleDeleteItem(item.id)}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Add Item Button */}
            <Button
                startIcon={<AddIcon />}
                onClick={handleAddItem}
                variant="outlined"
                sx={{
                    borderStyle: 'dashed',
                    textTransform: 'none',
                }}
            >
                Add Item
            </Button>

            {/* Summary */}
            {items.length > 0 && (
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                    <Box sx={{ minWidth: 300 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Subtotal:
                            </Typography>
                            <Typography variant="body2">
                                {formatCurrency(items.reduce((sum, item) => sum + item.amount, 0))}
                            </Typography>
                        </Box>
                        {items.reduce((sum, item) => sum + item.taxAmount, 0) > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Total Tax:
                                </Typography>
                                <Typography variant="body2">
                                    {formatCurrency(items.reduce((sum, item) => sum + item.taxAmount, 0))}
                                </Typography>
                            </Box>
                        )}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: 1, borderColor: 'divider' }}>
                            <Typography variant="h6">
                                Total:
                            </Typography>
                            <Typography variant="h6" color="primary">
                                {formatCurrency(items.reduce((sum, item) => sum + item.total, 0))}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default InlineItemsTable;
